<script setup lang="ts">
import { computed, ref, onMounted } from "vue";
import { useI18n } from "vue-i18n";

import { toast } from "@/common/utils/toast";

import {
  evaluationLaboratoryList,
  evaluationLaboratoryCreate,
  evaluationLaboratoryDelete,
} from "@/common/api/rest/evaluation/laboratory";

import type { EvaluationType } from "@/common/types/evaluation";
import type { LaboratoryListType } from "@/common/types/laboratory";

import { searchLaboratory } from "@/components";
import { baseConfirm, baseModal, baseTable } from "@/common/components";

const { t } = useI18n();

const emit = defineEmits(["update-data"]);

const props = withDefaults(
  defineProps<{
    data: EvaluationType | null;
    width?: string;
    showTitle?: boolean;
    isEdit?: boolean;
  }>(),
  {
    width: "100%",
    showTitle: true,
    isEdit: true,
  },
);

const tableHeader = computed(() => [
  { key: "name", label: t("common.labels.name") },
  { key: "document_number", label: t("common.labels.document") },
]);

const tableData = ref<any[]>([]);
const selected = ref<any | null>(null);
const selecteds = ref<any[]>([]);

const limit = ref<number>(5);
const page = ref<number>(0);
const pages = ref<number>(0);
const search = ref<string>("");
const searched = ref<number>(0);
const total = ref<number>(0);

const showModalAdd = ref(false);
const showConfirmDel = ref(false);

function fetchData() {
  if (!props.data) return;

  evaluationLaboratoryList(props.data.account_id, {
    evaluation_id: props.data.evaluation_id,
    limit: limit.value,
    page: page.value,
    search: search.value,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        tableData.value = data.list;
        page.value = data.infos.page;
        pages.value = data.infos.pages;
        total.value = data.infos.total;
        searched.value = data.infos.count;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function update() {
  emit("update-data");
}

function add() {
  selecteds.value = [];
  showModalAdd.value = true;
}

function addCancel() {
  selecteds.value = [];
  showModalAdd.value = false;
}

function addSelect(data: any[]) {
  selecteds.value = data;
}

function addConfirm() {
  if (!props.data || !selecteds.value.length) return;

  showModalAdd.value = false;

  const promises = selecteds.value.map((sl) =>
    evaluationLaboratoryCreate(props.data.account_id, {
      evaluation_id: props.data.evaluation_id,
      laboratory_id: sl.laboratory_id,
    }),
  );

  Promise.all(promises)
    .then((responses) => {
      responses.forEach((response) => {
        const { success, message } = response;
        if (success) {
          toast({ message: message, type: "success" });
          update();
          addCancel();
          fetchData();
        } else {
          toast({ message: message, type: "error" });
        }
      });
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function del(data: LaboratoryListType) {
  selected.value = data;
  showConfirmDel.value = true;
}

function delCancel() {
  selected.value = null;
  showConfirmDel.value = false;
}

function delConfirm() {
  if (!selected.value) return;

  showConfirmDel.value = false;

  evaluationLaboratoryDelete(props.data.account_id, selected.value.item_id)
    .then((response) => {
      const { success, message } = response;
      if (success) {
        update();
        fetchData();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function searchItem(value: string) {
  search.value = value;
  fetchData();
}

function changePage(value: number) {
  page.value = value;
  fetchData();
}

onMounted(() => {
  fetchData();
});
</script>

<template>
  <section class="tab-component" :class="{ width: width }">
    <template v-if="showTitle">
      <div class="title">
        {{ t("common.labels.laboratories") }}
      </div>
    </template>

    <div class="content">
      <baseTable
        :header="tableHeader"
        :data="tableData"
        :limit="limit"
        :page="page"
        :pages="pages"
        :total="total"
        :searched="searched"
        :options="true"
        :add="isEdit"
        :del="isEdit"
        @change:page="changePage"
        @searching="searchItem"
        @btn:add="add()"
        @btn:del="del"
      >
        <template #document_number="{ data }">
          {{ data.document_name }} -
          {{ data.document_number }}
        </template>
      </baseTable>
    </div>

    <base-modal
      :show="showModalAdd"
      :title="
        t('common.labels.add', {
          concat: t('common.labels.laboratory'),
        }).toUpperCase()
      "
      :show-button-confirm="true"
      :button-confirm="t('common.labels.add')"
      :button-close="t('common.labels.cancel')"
      @close="addCancel()"
      @confirm="addConfirm()"
    >
      <template #content>
        <search-laboratory
          :account_id="data?.account_id"
          :ids="tableData.map((item) => item.laboratory_id)"
          @update-data="addSelect"
        />
      </template>
    </base-modal>

    <base-confirm
      :show="showConfirmDel"
      max-width="400px"
      :title="
        t('common.labels.remove', {
          concat: t('common.labels.laboratory'),
        }).toUpperCase()
      "
      :message="
        t('common.texts.remove_message', {
          concat: t('common.labels.laboratory'),
        })
      "
      @cancel="delCancel"
      @confirm="delConfirm"
    />
  </section>
</template>

<style lang="scss" scoped>
.tab-component {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  .title {
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    font-size: 1.15rem;
    font-weight: bold;
    text-transform: uppercase;
    border-bottom: 1px solid var(--border);
    padding-block-end: 0.5rem;
  }
}
</style>
